import React from 'react';
import { ID, withCodes, l } from 'app/blocks/common/codes';
import { fetchItems, PAGE_SIZE, sanitise } from './api';
import type { StaticSearchErrorKind } from './api';
import StaticSearchAutocomplete from './AutoComplete/static-search-autocomplete';
import bgMagnifierLeft from './icons/bg-magnifier-left.svg';
import bgMagnifierRight from './icons/bg-magnifier-right.svg';

import './static-search.scss';

export const SEARCH_DEBOUNCE_MS = 300;

const emptyOptions = [] as any;

function StaticSearch() {
    const searchResourcesHeadingId = React.useId();
    const [inputValue, setInputValue] = React.useState<string>('');
    const [options, setOptions] = React.useState<readonly Search.Static[]>(emptyOptions);
    const [searchError, setSearchError] = React.useState<StaticSearchErrorKind | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [hasMore, setHasMore] = React.useState(false);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const [loadMoreError, setLoadMoreError] = React.useState(false);
    const [loadMoreErrorVariant, setLoadMoreErrorVariant] = React.useState<'connection' | 'generic'>('generic');
    const [hasSearched, setHasSearched] = React.useState(false);

    const sanitisedQuery = sanitise(inputValue);

    const isLoadingMoreRef = React.useRef(isLoadingMore);
    React.useEffect(() => {
        isLoadingMoreRef.current = isLoadingMore;
    }, [isLoadingMore]);

    React.useEffect(() => {
        if (isLoadingMoreRef.current) return undefined;

        setLoadMoreError(false);

        if (sanitisedQuery.length < 3) {
            setIsLoading(false);
            setSearchError(null);
            setOptions(emptyOptions);
            setHasMore(false);
            setIsLoadingMore(false);
            setHasSearched(false);
            return undefined;
        }

        let active = true;

        const run = async () => {
            const { errorKind, staticItems } = await fetchItems({
                onStart: () => {
                    if (active) {
                        setIsLoading(true);
                    }
                },
                query: sanitisedQuery,
                timeout: SEARCH_DEBOUNCE_MS,
            });

            if (!active) {
                return;
            }

            setIsLoading(false);
            setHasSearched(true);

            if (errorKind) {
                setSearchError(errorKind);
                setOptions(emptyOptions);
                setHasMore(false);
                return;
            }

            setSearchError(null);

            const sliced = (staticItems ?? []).slice(0, PAGE_SIZE);
            setOptions(sliced);
            const newHasMore = (staticItems?.length ?? 0) >= PAGE_SIZE;
            setHasMore(newHasMore);
        };

        run();

        return () => {
            active = false;
        };
    }, [sanitisedQuery]);

    const loadMore = React.useCallback(async () => {
        if (isLoadingMore || isLoading || !hasMore) return;
        setIsLoadingMore(true);
        setLoadMoreError(false);
        const offset = options.length;

        const { errorKind, staticItems } = await fetchItems({
            offset,
            onStart: () => {},
            query: sanitisedQuery,
            size: PAGE_SIZE,
        });
        setIsLoadingMore(false);
        if (errorKind) {
            setLoadMoreError(true);
            setLoadMoreErrorVariant(errorKind);
            return;
        }
        if (staticItems) {
            const newHasMore = staticItems.length >= PAGE_SIZE;
            setHasMore(newHasMore);
            setOptions(prev => [...prev, ...staticItems]);
        }
    }, [hasMore, isLoading, isLoadingMore, options.length, sanitisedQuery]);

    return (
        <section aria-labelledby={searchResourcesHeadingId} className="StaticSearch">
            <div aria-hidden className="StaticSearch-bgMagnifiers">
                <img
                    alt=""
                    aria-hidden
                    className="StaticSearch-bgMagnifier StaticSearch-bgMagnifier--left"
                    decoding="async"
                    src={bgMagnifierLeft}
                />
                <img
                    alt=""
                    aria-hidden
                    className="StaticSearch-bgMagnifier StaticSearch-bgMagnifier--right"
                    decoding="async"
                    src={bgMagnifierRight}
                />
            </div>
            <h2 className="StaticSearch-heading" id={searchResourcesHeadingId}>
                {l('SEARCH_CODES.STATIC.HEADER')}
            </h2>
            <StaticSearchAutocomplete
                hasMore={hasMore}
                hasSearched={hasSearched}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                loadMoreError={loadMoreError}
                loadMoreErrorVariant={loadMoreErrorVariant}
                onChange={setInputValue}
                onLoadMore={loadMore}
                options={options}
                searchError={searchError}
                value={inputValue}
            />
        </section>
    );
}

export default withCodes(StaticSearch, ID.SEARCH_CODES);
